import moment from 'moment';

import texts from './texts.json';

const TODAY = moment().startOf('day');
const YESTERDAY = moment().subtract(1, 'days').startOf('day');
const THIS_MONTH = moment().startOf('month');
const LAST_MONTH = moment().subtract(1, 'month').startOf('month');
const THIS_WEEK = moment().startOf('week');
const LAST_WEEK = moment().subtract(1, 'week').startOf('week');

const SECONDS_PER_HOUR = 3600;
/**
 * Calculates the growth (in percentage) between current and previous numbers
* @param {number} current - The current number
* @param {number} previous - The previous number
* @returns {number} The growth
*/
const calculateGrowthPercentage = (current, previous) => {
  if (current === 0 && previous === 0) {
    return 0;
  }
  if (previous === 0) {
    return 100;
  }
  return ((current - previous) / previous) * 100;
};

/**
 * @param {Firestore Timestamp} value - The value to check
 * @param {Date} start - The start date (inclusive)
 * @param {Date} end - The end date (exclusive)
 * @returns {number} 1 if `value` falls within [start, end), else 0
 */
const countIfInWindow = (value, start, end) => {
  if (!value) return 0;

  const m = moment(value.toDate ? value.toDate() : value);
  const inStart = m.isSameOrAfter(start);
  const inEnd = end ? m.isBefore(end) : true;

  return Number(inStart && inEnd);
};

/**
 * Calculates daily and monthly statistics for a collection of items based on a date field
 * @param {Object[]} items - Array of items to analyze
 * @param {string} dateField - The name of the field containing the date to analyze
 * @returns {Object[]} Array containing two stat objects:
 *   1. Daily stats object with:
 *      - count: Number of items from today
 *      - growth: Percentage growth compared to yesterday
 *      - isPositive: Whether today's count is >= yesterday's
 *   2. Monthly stats object with:
 *      - count: Number of items from this month
 *      - growth: Percentage growth compared to last month
 *      - isPositive: Whether this month's count is >= last month's
 */
const calculateStats = (items, dateField) => {
  const stats = {
    today: 0,
    yesterday: 0,
    thisMonth: 0,
    lastMonth: 0,
  };

  items.forEach((item) => {
    // If a lead is removed, exclude it from the win leads statistics
    if (dateField === texts.fieldName.wins && item.removed) {
      return;
    }
    const date = item[dateField];

    stats.today += countIfInWindow(date, TODAY);
    stats.yesterday += countIfInWindow(date, YESTERDAY, TODAY);
    stats.thisMonth += countIfInWindow(date, THIS_MONTH);
    stats.lastMonth += countIfInWindow(date, LAST_MONTH, THIS_MONTH);
  });

  return [
    {
      count: stats.today,
      growth: Math.round(calculateGrowthPercentage(stats.today, stats.yesterday)),
      isPositive: stats.today >= stats.yesterday,
    },
    {
      count: stats.thisMonth,
      growth: Math.round(calculateGrowthPercentage(stats.thisMonth, stats.lastMonth)),
      isPositive: stats.thisMonth >= stats.lastMonth,
    },
  ];
};

/**
 * Calculates weekly statistics for online sessions based on a date field
 * @param {Object[]} userSessions - Array of user sessions to analyze
 * @returns {Object[]} Array containing two stat objects:
 *   1. Weekly stats object with:
 *      - count: Total duration of online sessions from this week
 *      - growth: Percentage growth compared to last week
 *      - isPositive: Whether this week's duration is >= last week's duration
 */
const calculateOnlineStats = (userSessions) => {
  const stats = {
    thisWeek: 0,
    lastWeek: 0,
  };

  userSessions.forEach(({ onlineFrom, durationInSeconds }) => {
    if (!durationInSeconds || !onlineFrom) return;

    const sessionDate = moment(onlineFrom.toDate());
    if (sessionDate.isSameOrAfter(THIS_WEEK)) {
      stats.thisWeek += durationInSeconds;
    } else if (sessionDate.isSameOrAfter(LAST_WEEK)) {
      stats.lastWeek += durationInSeconds;
    }
  });

  // Convert seconds to hours
  const thisWeekHours = Math.round(stats.thisWeek / SECONDS_PER_HOUR);
  const lastWeekHours = Math.round(stats.lastWeek / SECONDS_PER_HOUR);

  return [
    {
      count: thisWeekHours,
      growth: Math.round(calculateGrowthPercentage(thisWeekHours, lastWeekHours)),
      isPositive: thisWeekHours >= lastWeekHours,
    },
  ];
};

const calculateWonLeadsStats = (leads) => calculateStats(leads, texts.fieldName.wins);
const calculateClaimedLeadsStats = (claimedLeadsHistory) => calculateStats(claimedLeadsHistory, texts.fieldName.claims);

export {
  calculateWonLeadsStats,
  calculateClaimedLeadsStats,
  calculateOnlineStats,
  LAST_MONTH,
  LAST_WEEK,
};
